import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { BetaChartCard } from 'src/pages/reports/components/beta-charts';
import { BetaChartAreaSkeleton } from 'src/pages/reports/components/beta-charts/beta-chart-skeletons';
import { formatCompactNumber, formatPlatformDisplayName } from './overview-beta-format';

export interface PlatformShareSlice {
  name: string;
  value: number;
  colour: string;
}

interface OverviewBetaPlatformShareProps {
  data: PlatformShareSlice[];
  metricLabel?: string;
  isLoading?: boolean;
}

export function OverviewBetaPlatformShare({
  data,
  metricLabel = 'Spend',
  isLoading = false,
}: OverviewBetaPlatformShareProps) {
  const total = data.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <BetaChartCard
      title="Platform Share"
      subtitle={`${metricLabel} mix across enabled platforms`}
      className="mb-0"
    >
      {isLoading ? (
        <BetaChartAreaSkeleton className="h-[220px]" />
      ) : data.length === 0 || total === 0 ? (
        <div className="flex h-[220px] items-center justify-center text-sm text-[#8A8EA0]">
          No platform data for the selected period.
        </div>
      ) : (
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="mx-auto h-[160px] w-[160px] shrink-0">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={48}
                  outerRadius={72}
                  paddingAngle={2}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {data.map((slice) => (
                    <Cell
                      key={slice.name}
                      fill={slice.colour}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [
                    formatCompactNumber(value, { currency: metricLabel === 'Spend' }),
                    metricLabel,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <table className="w-full border-collapse text-[12.5px]">
              <tbody>
                {data.map((slice) => {
                  const pct = total > 0 ? Math.round((slice.value / total) * 100) : 0;
                  return (
                    <tr
                      key={slice.name}
                      className="border-b border-[#EDEEF2] last:border-b-0"
                    >
                      <td className="py-2 align-middle">
                        <div className="flex items-center gap-2 font-semibold capitalize text-[#0D0F1A]">
                          <span
                            className="inline-block h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: slice.colour }}
                          />
                          {formatPlatformDisplayName(slice.name)}
                        </div>
                        <div className="mt-1 h-1 w-full rounded-sm bg-[#EDEEF2]">
                          <div
                            className="h-1 rounded-sm"
                            style={{ width: `${pct}%`, backgroundColor: slice.colour }}
                          />
                        </div>
                      </td>
                      <td className="py-2 pl-3 text-right align-middle">
                        <div className="font-mono text-[11.5px] font-semibold text-[#0D0F1A]">
                          {formatCompactNumber(slice.value, { currency: metricLabel === 'Spend' })}
                        </div>
                        <div className="text-[11px] text-[#9CA3AF]">
                          {pct}
                          %
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </BetaChartCard>
  );
}
